import { ArrowRight, FileText } from "lucide-react";
import { scrollToForm, trackEvent } from "@/lib/tracking";

const ACCENT = "#BEF264";

const FACTORS = [
  "Website and landing-page scope",
  "Number of customer journeys",
  "CRM and sales pipeline complexity",
  "Automated follow-up requirements",
  "Integrations",
  "Hosting and support arrangements",
  "Additional custom functionality",
];

const COST_TYPES = [
  { title: "Once-off implementation", copy: "Strategy, design, website build and sales system configuration, as set out in your proposal." },
  { title: "Ongoing subscription", copy: "Hosting, platform access and agreed support, where these form part of your plan." },
  { title: "Third-party fees", copy: "Domains, software subscriptions, messaging or payment-processing charges, billed separately." },
];

export default function SalesPlanCTA() {
  const getPlan = () => { trackEvent("cta_click", { location: "sales_pricing", label: "get_my_sales_system_plan" }); scrollToForm(); };

  return (
    <section id="pricing" aria-labelledby="pricing-heading" data-testid="web-pricing-section" className="relative py-20 sm:py-28 bg-white">
      <div className="mx-auto max-w-6xl px-5 sm:px-8">
        <div className="max-w-3xl">
          <div className="inline-flex items-center gap-2 rounded-full border border-zinc-200 bg-zinc-50 px-3.5 py-1.5 text-[11px] tracking-[0.14em] uppercase text-zinc-600 font-semibold mb-6">
            <FileText className="h-3.5 w-3.5 text-zinc-500" /> Business website development cost
          </div>
          <h2 id="pricing-heading" className="font-display text-4xl sm:text-5xl font-black tracking-[-0.03em] leading-[1.04] text-zinc-900">
            Website &amp; Sales System Pricing
          </h2>
          <p className="mt-5 text-zinc-600 leading-relaxed text-base sm:text-lg">
            Every business has different website requirements, sales processes and integration needs. Rekisa prepares a tailored
            implementation proposal based on the pages, forms, CRM functions, follow-up workflows and integrations your business requires.
          </p>
        </div>

        <div className="mt-12 grid lg:grid-cols-[1fr_1.1fr] gap-6">
          <div className="rounded-3xl border border-zinc-200 p-7" data-testid="sales-pricing-factors">
            <h3 className="font-display text-xl font-black text-zinc-900">What pricing depends on</h3>
            <ul className="mt-5 space-y-2.5">
              {FACTORS.map((f) => (
                <li key={f} className="flex items-start gap-2.5 text-[15px] text-zinc-700">
                  <span className="mt-2 h-1.5 w-1.5 rounded-full bg-zinc-900 shrink-0" /> {f}
                </li>
              ))}
            </ul>
          </div>
          <div className="rounded-3xl bg-sales-navy text-white p-7 sm:p-9 flex flex-col">
            <h3 className="font-display text-xl font-black">How costs are separated</h3>
            <div className="mt-5 space-y-3 flex-1">
              {COST_TYPES.map((c, i) => (
                <div key={c.title} data-testid={`sales-cost-type-${i}`} className="rounded-2xl border border-white/10 bg-white/[0.04] px-4 py-3.5">
                  <div className="text-sm font-bold" style={{ color: ACCENT }}>{c.title}</div>
                  <p className="mt-1 text-[13px] text-white/65 leading-snug">{c.copy}</p>
                </div>
              ))}
            </div>
            <button onClick={getPlan} data-testid="sales-plan-cta-primary"
              className="mt-7 inline-flex self-start items-center gap-1.5 rounded-full min-h-12 px-7 py-3 text-base font-bold text-[#0B1120] hover:brightness-105 transition-[filter]"
              style={{ background: ACCENT }}>
              Get My Sales System Plan <ArrowRight className="h-5 w-5" />
            </button>
          </div>
        </div>
      </div>
    </section>
  );
}
